// @ts-nocheck
/**
 * 市場インデックス管理
 *
 * カードを属性/レアリティ/カテゴリ/コスト別にグループ化
 * ファンド指数やFXレートの計算を高速化するためのインデックス
 *
 * 設計: market_economy_system_v3.md
 */

import { ATTRIBUTES } from './constants';

// ========================================
// インデックス構築
// ========================================

/**
 * カードリストからマーケットインデックスを構築
 *
 * @param {Object[]} cards - 全カードリスト
 * @returns {Object} マーケットインデックス
 */
export function buildMarketIndex(cards) {
  const index = {
    // 属性別
    byAttribute: {},
    // カテゴリ別
    byCategory: {},
    // コスト別（低コスト/高コスト）
    byCost: {
      low: [],   // cost <= 2
      high: [],  // cost >= 5
    },
    // 全カード参照
    allCards: cards,
  };

  // 属性の初期化
  for (const attr of ATTRIBUTES) {
    index.byAttribute[attr] = [];
  }

  // カードを分類
  for (const card of cards) {
    // 属性別
    const attribute = normalizeAttribute(card.attribute);
    if (index.byAttribute[attribute]) {
      index.byAttribute[attribute].push(card);
    }

    // カテゴリ別
    const categories = extractCategories(card.category);
    for (const category of categories) {
      if (!index.byCategory[category]) {
        index.byCategory[category] = [];
      }
      index.byCategory[category].push(card);
    }

    // コスト別
    const cost = parseInt(card.cost) || 0;
    if (cost <= 2) {
      index.byCost.low.push(card);
    }
    if (cost >= 5) {
      index.byCost.high.push(card);
    }
  }

  return index;
}

/**
 * 属性を正規化
 *
 * @param {string} attribute - 属性
 * @returns {string} 正規化された属性
 */
function normalizeAttribute(attribute) {
  const raw = attribute?.trim?.() || attribute;
  return (raw && raw !== '') ? raw : 'なし';
}

/**
 * カテゴリを抽出
 *
 * @param {string|string[]} category - カテゴリ（文字列または配列）
 * @returns {string[]} カテゴリ名の配列
 */
function extractCategories(category) {
  if (!category) return [];

  // 配列の場合
  if (Array.isArray(category)) {
    return category.map((c) => c.replace(/【|】/g, '').trim()).filter(Boolean);
  }

  // 文字列の場合（【カテゴリ】形式を抽出）
  const matches = category.match(/【([^】]+)】/g);
  if (matches) {
    return matches.map((m) => m.replace(/【|】/g, '').trim());
  }

  return [];
}

// ========================================
// インデックス取得ヘルパー
// ========================================

/**
 * 属性別カードリストを取得
 *
 * @param {Object} index - マーケットインデックス
 * @param {string} attribute - 属性
 * @returns {Object[]} カードリスト
 */
export function getCardsByAttribute(index, attribute) {
  return index.byAttribute[attribute] || [];
}

/**
 * カテゴリ別カードリストを取得
 *
 * @param {Object} index - マーケットインデックス
 * @param {string} category - カテゴリ
 * @returns {Object[]} カードリスト
 */
export function getCardsByCategory(index, category) {
  // カテゴリ名を正規化（【】を除去）
  const normalizedCategory = category.replace(/【|】/g, '').trim();
  return index.byCategory[normalizedCategory] || [];
}

/**
 * 複数カテゴリのカードリストを取得（重複なし）
 *
 * @param {Object} index - マーケットインデックス
 * @param {string[]} categories - カテゴリ配列
 * @returns {Object[]} カードリスト（重複なし）
 */
export function getCardsByCategories(index, categories) {
  if (!categories || categories.length === 0) {
    return [];
  }

  // カードIDで重複を排除
  const cardMap = new Map();

  for (const category of categories) {
    const normalizedCategory = category.replace(/【|】/g, '').trim();
    const cards = index.byCategory[normalizedCategory] || [];
    for (const card of cards) {
      if (!cardMap.has(card.id)) {
        cardMap.set(card.id, card);
      }
    }
  }

  return Array.from(cardMap.values());
}

/**
 * 低コストカードリストを取得（cost <= 2）
 *
 * @param {Object} index - マーケットインデックス
 * @returns {Object[]} カードリスト
 */
export function getLowCostCards(index) {
  return index.byCost.low;
}

/**
 * 高コストカードリストを取得（cost >= 5）
 *
 * @param {Object} index - マーケットインデックス
 * @returns {Object[]} カードリスト
 */
export function getHighCostCards(index) {
  return index.byCost.high;
}

/**
 * 全カードを取得
 *
 * @param {Object} index - マーケットインデックス
 * @returns {Object[]} カードリスト
 */
export function getAllCards(index) {
  return index.allCards;
}

// ========================================
// エクスポート
// ========================================

export default {
  buildMarketIndex,
  getCardsByAttribute,
  getCardsByCategory,
  getCardsByCategories,
  getLowCostCards,
  getHighCostCards,
  getAllCards,
};
